import { Card, Col, Form, Input, Row, Menu, Modal, Tabs, Table, Tag, Select, Button, Space, Typography, message } from 'antd';
import { DownloadOutlined, SendOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ChartLoader from 'components/ChartLoader';
import { useTranslation } from 'react-i18next'
import Paho from 'paho-mqtt'
import getWebsocketServer from 'utils/websocket'
import { getItem } from 'utils/tokenStore'

const randR = () => {
  let res = [];
  let len = 100;
  while (len--) {
    res.push(0)
  }
  return res;
};

const gen2STime = () => {
  let now = new Date();
  let res = [];
  let len = 100;
  while (len--) {
    res.unshift(now.toLocaleTimeString().replace(/^\D*/, ""));
    now = new Date(+now - 2000);
  }
  return res;
};

// Per DASHBOARD_ERROR_LOG_INTEGRATION_2.pdf - Error/Warning/Info color coding.
const LEVEL_META = {
  E: { color: 'red', label: 'Error' },
  W: { color: 'gold', label: 'Warning' },
  I: { color: 'default', label: 'Info' },
};

const DURATION_PRESETS = [5, 15, 30, 60];

const DeviceDebugModal = (props) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const [chartLoading, setChartLoading] = useState(true)
  const [showCustomPicker, setShowCustomPicker] = useState(false)

  const mac = props.selectedDevice?.MAC

  // --- Error log (live tail via direct MQTT subscribe, history via backend) ---
  const [liveErrorLog, setLiveErrorLog] = useState([])
  const mqttClientRef = useRef(null)

  // --- Serial log streaming ---
  const [streamDuration, setStreamDuration] = useState(30)
  const [streamStatus, setStreamStatus] = useState('idle') // idle | streaming | done
  const [streamSecondsLeft, setStreamSecondsLeft] = useState(0)
  const [streamLines, setStreamLines] = useState('')
  const streamTimerRef = useRef(null)
  const serialTextRef = useRef(null)

  useEffect(() => {
    if (!props.visible || !mac) return

    props.getErrorLog(mac)
    setLiveErrorLog([])

    let client = null
    try {
      const clientId = `debug-${mac}-${Date.now()}`
      client = new Paho.Client(getWebsocketServer(), clientId)
      client.connect({
        timeout: 3,
        userName: getItem("Username"),
        password: "c764eb2b5fa2d259dc667e2b9e195218",
        onSuccess: () => {
          client.subscribe(`/GMT/DEV/${mac}/LOG`)
          client.subscribe(`/GMT/DEV/${mac}/LOG/SERIAL`)
          client.subscribe(`/GMT/DEV/${mac}/LOG/SERIAL/END`)
          client.onMessageArrived = (msg) => {
            const topic = msg.destinationName
            if (topic.endsWith('/LOG/SERIAL/END')) {
              const match = msg.payloadString.match(/done:(\d+)s/)
              const seconds = match ? match[1] : '?'
              setStreamLines((prev) => prev + `\n${t('DevicePage.debugModal.serialLog.streamEnded', { seconds })}\n`)
              setStreamStatus('done')
              if (streamTimerRef.current) {
                clearInterval(streamTimerRef.current)
                streamTimerRef.current = null
              }
            } else if (topic.endsWith('/LOG/SERIAL')) {
              setStreamLines((prev) => prev + msg.payloadString)
            } else if (topic.endsWith('/LOG')) {
              try {
                const parsed = JSON.parse(msg.payloadString)
                setLiveErrorLog((prev) => [{ ...parsed, RECEIVED_TIME: new Date().toISOString() }, ...prev])
              } catch (e) {
                // ignore malformed payload
              }
            }
          }
        },
        onFailure: (err) => console.error('MQTT connect failed for device debug modal:', err),
      })
      mqttClientRef.current = client
    } catch (e) {
      console.error('Failed to set up MQTT client for device debug modal:', e)
    }

    return () => {
      if (streamTimerRef.current) {
        clearInterval(streamTimerRef.current)
        streamTimerRef.current = null
      }
      try {
        client && client.isConnected() && client.disconnect()
      } catch (e) {
        // already disconnected
      }
      mqttClientRef.current = null
    }
  }, [props.visible, mac])

  useEffect(() => {
    if (serialTextRef.current) {
      serialTextRef.current.scrollTop = serialTextRef.current.scrollHeight
    }
  }, [streamLines])

  const combinedErrorLog = [
    ...liveErrorLog,
    ...(props.errorLog || []).map((row) => ({ ts: row.TS, lv: row.LEVEL, src: row.SRC, msg: row.MSG, RECEIVED_TIME: row.RECEIVED_TIME })),
  ]

  const errorLogColumns = [
    {
      key: 'lv',
      title: t('DevicePage.debugModal.errorLog.level'),
      render: (_, row) => {
        const meta = LEVEL_META[row.lv] || { color: 'default', label: row.lv }
        return <Tag color={meta.color}>{meta.label}</Tag>
      },
    },
    {
      key: 'src',
      title: t('DevicePage.debugModal.errorLog.source'),
      dataIndex: 'src',
    },
    {
      key: 'msg',
      title: t('DevicePage.debugModal.errorLog.message'),
      dataIndex: 'msg',
    },
    {
      key: 'time',
      title: t('DevicePage.debugModal.errorLog.time'),
      render: (_, row) => (
        row.ts ? new Date(row.ts * 1000).toLocaleString() : t('DevicePage.debugModal.errorLog.noTimestamp')
      ),
    },
  ];

  const handleTestLog = () => {
    if (mac) props.triggerTestLog(mac)
  }

  const handleLogLevelChange = (level) => {
    if (mac) props.setLogLevel(mac, level)
  }

  const handleStartStream = () => {
    if (!mac) return
    setStreamLines('')
    setStreamStatus('streaming')
    setStreamSecondsLeft(streamDuration)
    props.startLogStream(mac, streamDuration)

    if (streamTimerRef.current) clearInterval(streamTimerRef.current)
    streamTimerRef.current = setInterval(() => {
      setStreamSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(streamTimerRef.current)
          streamTimerRef.current = null
          setStreamStatus('done')
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleStopStream = () => {
    if (!mac) return
    props.stopLogStream(mac)
    if (streamTimerRef.current) {
      clearInterval(streamTimerRef.current)
      streamTimerRef.current = null
    }
    setStreamStatus('done')
  }

  const handleDownload = () => {
    const blob = new Blob([streamLines], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    a.href = url
    a.download = `serial_log_${mac}_${timestamp}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const statusLabel = streamStatus === 'streaming'
    ? t('DevicePage.debugModal.serialLog.status.streaming', { seconds: streamSecondsLeft })
    : streamStatus === 'done'
      ? t('DevicePage.debugModal.serialLog.status.done')
      : t('DevicePage.debugModal.serialLog.status.idle')

  const handleOption = (item) => {

    console.log("Selected option:", item.key);

    if (mode != item.key){
      setMode(item.key)
    }

    if (item.key === 'CUSTOM') {
      setShowCustomPicker(true)
      return
    }

    setShowCustomPicker(false)

    if (item.key){
      if (props.selectedDevice && props.selectedDevice.MAC){
        if (props.data){
          setChartLoading(true)
        }
        props.action(props.selectedDevice.MAC,item.key)
      }
    }else{
      if (dateRange[0] == null || dateRange[1] == null){
        message.error('Please select date range!!');
      }else{
        if (props.data){
          setChartLoading(true)
        }
        const fromDate = new Date(dateRange[0]);
        const fromyear = fromDate.getFullYear();
        const frommonth = String(fromDate.getMonth() + 1).padStart(2, '0');
        const fromday = String(fromDate.getDate()).padStart(2, '0');

        const fromFormattedDate = `${fromyear}-${frommonth}-${fromday}`;

        const toDate = new Date(dateRange[1]);
        const toyear = toDate.getFullYear();
        const tomonth = String(toDate.getMonth() + 1).padStart(2, '0');
        const today = String(toDate.getDate()).padStart(2, '0');

        const toFormattedDate = `${toyear}-${tomonth}-${today}`;
        props.action(props.selectedDevice.MAC,"CUSTOM",fromFormattedDate,toFormattedDate)
      }
    }
  }

  const [dateRange, setDateRange] = useState([null, null]);
  const [mode, setMode] = useState('1 WEEK')
  const [startDate, endDate] = dateRange;
  const chartRef = useRef(null);
  const defaultOptions = {
    textStyle: {
      color: "#000",
    },
    visualMap: [
      {
        show: false,
        type: "continuous",
        seriesIndex: 0,
        min: 80,
        max: 0,
      },
    ],
    title: [
      {
        left: "center",
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: {
      },
    },
    toolbox: {
      show: false,
      feature: {
        dataView: { readOnly: false },
        restore: {},
        saveAsImage: {},
      },
    },
    dataZoom: {
      show: false,
      start: 0,
      end: 100,
    },
    xAxis: [
      {
        axisLine: {
          show: false,
        },
        type: "category",
        boundaryGap: true,
        data: gen2STime(),
        splitLine: {
          show: false,
        },
      }
    ],
    yAxis: [
      {
        axisLine: {
          show: false,
        },
        type: "value",
        scale: true,
        max: 1,
        min: 0,
        boundaryGap: [0.2, 0.2],
        splitLine: {
          show: false,
        },
      }
    ],
    grid: [
      {
        bottom: "30%",
      },
    ],
    dataZoom: [
      {
        top: "80%",
        start: 0,
        end: 100,
        xAxisIndex: [0, 1],
        textStyle: {
          color: "#000"
        }
      },
    ],
    series: [
      {
        name: "Data Count",
        type: "bar",
        smooth: true,
        emphasis: {
          focus: "series",
        },
        data: randR(),
      }
    ],
  };
  const [option,setOption] = useState(defaultOptions)

  const onFinish = (values) => {
    console.log('Received values:', values);
    props.action(values);
  };

    const handleCustomDateChange = (update) => {
        setDateRange(update);

        if (update && update[0] && update[1]) {
            const fromDate = new Date(update[0]);
            const fromyear = fromDate.getFullYear();
            const frommonth = String(fromDate.getMonth() + 1).padStart(2, '0');
            const fromday = String(fromDate.getDate()).padStart(2, '0');
            const fromFormattedDate = `${fromyear}-${frommonth}-${fromday}`;

            const toDate = new Date(update[1]);
            const toyear = toDate.getFullYear();
            const tomonth = String(toDate.getMonth() + 1).padStart(2, '0');
            const today = String(toDate.getDate()).padStart(2, '0');
            const toFormattedDate = `${toyear}-${tomonth}-${today}`;

            if (props.selectedDevice && props.selectedDevice.MAC) {
                setChartLoading(true)
            }
            props.action(props.selectedDevice.MAC, "CUSTOM", fromFormattedDate, toFormattedDate)
        }
    }

    useEffect(() => {
        if (props.selectedDevice && props.selectedDevice.MAC) {
            console.log("Fetching device temperature for MAC:", props.selectedDevice.MAC);
            setChartLoading(true)
            props.action(props.selectedDevice.MAC, "1 HOUR")
        }
    }, [props.selectedDevice]);

    useEffect(() => {
  if (!props.data || !props.data.DATA || props.data.DATA.length === 0) {
    setOption({
      ...defaultOptions,
      yAxis: [
        {
          type: "value",
          scale: true,
          axisLine: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: "Temperature",
          type: "line",
          smooth: true,
          data: [],
        },
      ],
    });

    setChartLoading(false);
    return;
  }

  const timeStart = Number(
    Array.isArray(props.data.TIME_START)
      ? props.data.TIME_START[0]
      : props.data.TIME_START
  );

  const values = String(props.data.DATA[0])
    .split(";")
    .filter((v) => v !== "")
    .map(Number);


  const times = values.map((_, index) => {
    const d = new Date((timeStart + index * 60) * 1000);

    return (
      d.getFullYear() +
      "-" +
      String(d.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(d.getDate()).padStart(2, "0") +
      " " +
      String(d.getHours()).padStart(2, "0") +
      ":" +
      String(d.getMinutes()).padStart(2, "0")
    );
  });

  console.log("Times:", times);
  console.log("Values:", values);

  setOption({
    ...defaultOptions,

    xAxis: [
      {
        type: "category",
        boundaryGap: false,
        data: times,
        axisLine: {
          show: false,
        },
      },
    ],

    yAxis: [
      {
        type: "value",
        scale: true,
        axisLine: {
          show: false,
        },
        splitLine: {
          show: true,
        },
      },
    ],

    series: [
      {
        name: "Temperature",
        type: "line",
        smooth: true,
        showSymbol: false,
        data: values,
      },
    ],
  });

  setChartLoading(false);
}, [props.data]);

  const temperaturePane = (
    <Card
        title={t("DevicePage.table.temperature")}
        style={{ height: "100%"}}
        className='content-card'
        extra={
            <Menu
            style={{width:'100%'}}
            theme="light"
            mode="horizontal"
            onClick={handleOption}
            defaultSelectedKeys={['1 HOUR']}
            >
            <Menu.Item key="1 HOUR">{t("location_history.hour")}</Menu.Item>
            <Menu.Item key="1 DAY">{t("location_history.day")}</Menu.Item>
            <Menu.Item key="1 WEEK">{t("location_history.week")}</Menu.Item>
            <Menu.Item key="1 MONTH">{t("location_history.month")}</Menu.Item>
            <Menu.Item key="CUSTOM">{t("location_history.custom")}</Menu.Item>
            </Menu>
        }
        >
        <Row>
            <Col span={24}>
            {showCustomPicker && (
                <div style={{ marginBottom: 12, padding: 12, border: '1px solid #d9d9d9', borderRadius: 8, background: '#fff' }}>
                    <DatePicker
                        selectsRange={true}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={handleCustomDateChange}
                        isClearable={true}
                    />
                </div>
            )}
            <ReactECharts
              ref={chartRef}
              option={option}
              style={{ height: 400, width: 500 }}
              notMerge={true}
              lazyUpdate={false}
            />
            </Col>
        </Row>
        {
            chartLoading && <ChartLoader/>
        }
        </Card>
  )

  const errorLogPane = (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<SendOutlined />} onClick={handleTestLog}>
          {t('DevicePage.debugModal.errorLog.testLog')}
        </Button>
        <Typography.Text>{t('DevicePage.debugModal.errorLog.logLevel')}:</Typography.Text>
        <Select
          style={{ width: 160 }}
          value={props.selectedDevice?.LOG_LEVEL ?? undefined}
          placeholder={t('DevicePage.debugModal.errorLog.logLevel')}
          onChange={handleLogLevelChange}
          options={[0, 1, 2, 3].map((level) => ({
            value: level,
            label: t(`DevicePage.debugModal.errorLog.levels.${level}`),
          }))}
        />
      </Space>
      <Table
        dataSource={combinedErrorLog}
        columns={errorLogColumns}
        rowKey={(row, index) => `${row.RECEIVED_TIME}-${index}`}
        size="small"
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: t('DevicePage.debugModal.errorLog.noLogs') }}
      />
    </div>
  )

  const serialLogPane = (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Typography.Text>{t('DevicePage.debugModal.serialLog.duration')}:</Typography.Text>
        <Select
          style={{ width: 100 }}
          value={streamDuration}
          onChange={setStreamDuration}
          disabled={streamStatus === 'streaming'}
          options={DURATION_PRESETS.map((s) => ({ value: s, label: `${s}s` }))}
        />
        <Input
          type="number"
          style={{ width: 100 }}
          min={1}
          max={300}
          value={streamDuration}
          disabled={streamStatus === 'streaming'}
          onChange={(e) => setStreamDuration(Number(e.target.value))}
        />
        <Button type="primary" onClick={handleStartStream} disabled={streamStatus === 'streaming'}>
          {t('DevicePage.debugModal.serialLog.start')}
        </Button>
        <Button onClick={handleStopStream} disabled={streamStatus !== 'streaming'}>
          {t('DevicePage.debugModal.serialLog.stop')}
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleDownload} disabled={!streamLines}>
          {t('DevicePage.debugModal.serialLog.download')}
        </Button>
      </Space>
      <div style={{ marginBottom: 8 }}>
        <Tag color={streamStatus === 'streaming' ? 'processing' : streamStatus === 'done' ? 'green' : 'default'}>
          {statusLabel}
        </Tag>
      </div>
      <div
        ref={serialTextRef}
        style={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          background: '#111',
          color: '#0f0',
          padding: 12,
          height: 320,
          overflowY: 'auto',
          borderRadius: 4,
          fontSize: 12,
        }}
      >
        {streamLines}
      </div>
    </div>
  )

  return (
    <Modal
      title={t("DevicePage.debugModal.title")}
      open={props.visible}
      onOk={props.close}
      okText={t("Ok")}
      onCancel={props.close}
      width={800}
    >
      <Form
        form={form}
        onFinish={onFinish}
        initialValues={props.selectedDevice}
        layout="vertical"
      >
        <p>
          {t("DevicePage.deviceModal.macAddress")}:
          <span style={{ fontWeight: 'bold' }}>
            {props.selectedDevice ? props.selectedDevice.MAC : ''}
          </span>
        </p>
      </Form>

      <Tabs
        items={[
          { key: 'temperature', label: t('DevicePage.debugModal.tabs.temperature'), children: temperaturePane },
          { key: 'errorLog', label: t('DevicePage.debugModal.tabs.errorLog'), children: errorLogPane },
          { key: 'serialLog', label: t('DevicePage.debugModal.tabs.serialLog'), children: serialLogPane },
        ]}
      />
    </Modal>
  );
};

export default DeviceDebugModal;
